import { useEffect, useState } from 'react';
import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import {
  REASON_MAX_LENGTH,
  REASON_MIN_LENGTH,
  type BulkEditAssignment,
  type BulkEditPreviewResponse,
  type Finding,
  type LowRiskFieldValue,
} from '@complan/contracts';

import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FindingsAlert } from '@/features/admin/FindingsAlert';
import { TextArea } from '@/features/initiative/intake/fields';

/**
 * The Bulk_Edit dialog: choose the assignment, preview, then apply
 * (`complan-governance` R10.4, R10.6, R10.12, R10.14, R10.15).
 *
 * ## Two steps, and the second cannot be reached without the first
 *
 * R10.15: an apply that was not previewed in the same interaction is refusable, and the
 * server refuses it by requiring the token the preview issued. The dialog makes that
 * structural rather than advisory - there is no Apply control until a preview response is
 * in hand, and changing the assignment discards the preview, because a token describes
 * *those* changes to *those* versions and nothing else.
 *
 * ## The preview shows before and after, per record
 *
 * R10.4 requires the affected records and their before/after values. Showing only a count
 * would make the reader confirm a number rather than a change: "412 records" says nothing
 * about what is about to happen to them, and a mis-picked value looks identical to a
 * correct one at that resolution.
 *
 * ## Rejections are part of the preview, not an error
 *
 * R10.5 and R10.8 mean a selection can contain records the operation will decline - not
 * readable, cancelled, a deactivated value, a failing rule, a version that moved. The
 * preview lists them with their reason *before* the reader commits, so applying is a
 * decision about a known partial outcome rather than a surprise afterwards. A
 * `not_readable` rejection deliberately carries no reference or title: R10.5 forbids
 * disclosing whether an Initiative the requester may not read exists.
 *
 * ## The cap rejection travels in the preview, too
 *
 * R10.6's cap is reported as `maxRecords` and `requestedCount` in the preview response
 * rather than as a 422, so the reader is told "212 of a maximum 200" and can narrow the
 * selection - which is actionable, where a failed request is not.
 */
export interface BulkEditDialogProps {
  readonly isOpen: boolean;
  /** How many rows the reader selected, for the summary before a preview exists. */
  readonly selectedCount: number;
  /** The selectable values per field, resolved by the caller from master data. */
  readonly options: Readonly<Record<LowRiskFieldValue, readonly { id: string; name: string }[]>>;
  readonly preview: BulkEditPreviewResponse | undefined;
  readonly isPreviewing: boolean;
  readonly isApplying: boolean;
  readonly previewFindings: readonly Finding[];
  readonly applyFindings: readonly Finding[];
  /** True when the apply was refused because the preview no longer matches (R10.9, R10.15). */
  readonly isStale: boolean;
  readonly onPreview: (assignment: BulkEditAssignment) => void;
  readonly onApply: (reason: string) => void;
  readonly onClose: () => void;
}

/** The five fields R10.1 admits, in the order the dialog offers them. */
const FIELDS: readonly LowRiskFieldValue[] = [
  'priorityId',
  'categoryId',
  'initiativeTypeId',
  'ownerUserId',
  'backupOwnerUserId',
];

/** Only Backup Owner accepts a clearing null (R10.1's nullable `value`). */
const CLEARABLE_FIELDS: readonly LowRiskFieldValue[] = ['backupOwnerUserId'];

const CLEAR_VALUE = '__clear__';

export function BulkEditDialog({
  isOpen,
  selectedCount,
  options,
  preview,
  isPreviewing,
  isApplying,
  previewFindings,
  applyFindings,
  isStale,
  onPreview,
  onApply,
  onClose,
}: BulkEditDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [field, setField] = useState<LowRiskFieldValue>('priorityId');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setField('priorityId');
      setValue('');
      setReason('');
    }
  }, [isOpen]);

  const fieldOptions = options[field];
  const canClear = CLEARABLE_FIELDS.includes(field);
  const hasValue = value !== '';
  const overCap = preview !== undefined && preview.requestedCount > preview.maxRecords;
  const trimmedReason = reason.trim();
  const reasonValid =
    trimmedReason.length >= REASON_MIN_LENGTH && trimmedReason.length <= REASON_MAX_LENGTH;

  const assignment: BulkEditAssignment | null = hasValue
    ? { field, value: value === CLEAR_VALUE ? null : value }
    : null;

  return (
    <AriaModalDialog
      variant="centre"
      modalClassName="m-auto max-h-[90vh] w-full max-w-3xl rounded-[var(--radius-card)]"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={t('bulkEdit.title')}
    >
      <Heading slot="title" className="text-txt text-[15px] font-semibold">
        {t('bulkEdit.title')}
      </Heading>
      <p className="text-muted-foreground mt-2 text-[12.3px]">
        {t('bulkEdit.selected', { count: selectedCount })}
      </p>

      <FindingsAlert findings={previewFindings} />
      <FindingsAlert findings={applyFindings} />

      {isStale ? (
        <Alert variant="destructive" className="mt-2">
          <AlertTitle>{t('bulkEdit.stale.title')}</AlertTitle>
          {/* R10.9, R10.15: a record moved, or the token no longer matches. The remedy is
              to preview again, which is what the message says rather than "try again". */}
          <AlertDescription>{t('bulkEdit.stale.body')}</AlertDescription>
        </Alert>
      ) : null}

      {/* Step 1: the assignment. R10.1's five fields, one at a time. */}
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-txt text-[11.5px] font-semibold">{t('bulkEdit.field')}</span>
          <select
            className="border-line bg-panel text-txt h-8 rounded-[var(--radius-control)] border px-2 text-[12.5px]"
            value={field}
            onChange={(event) => {
              setField(event.target.value as LowRiskFieldValue);
              // The value belongs to the old field's vocabulary, and the preview described
              // the old assignment: both are discarded together (R10.15).
              setValue('');
            }}
          >
            {FIELDS.map((option) => (
              <option key={option} value={option}>
                {t(`bulkEdit.fieldLabel.${option}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-txt text-[11.5px] font-semibold">{t('bulkEdit.value')}</span>
          <select
            className="border-line bg-panel text-txt h-8 min-w-[180px] rounded-[var(--radius-control)] border px-2 text-[12.5px]"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          >
            <option value="">{t('bulkEdit.choose')}</option>
            {/* Only where the target field is nullable: offering "clear" for a required
                field would be offering an assignment the per-record hard validation of
                R10.8 is guaranteed to reject. */}
            {canClear ? <option value={CLEAR_VALUE}>{t('bulkEdit.clearValue')}</option> : null}
            {fieldOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>

        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="bulk-edit-preview"
          disabled={assignment === null || isPreviewing}
          onClick={() => {
            if (assignment !== null) {
              onPreview(assignment);
            }
          }}
        >
          {isPreviewing ? t('bulkEdit.previewing') : t('bulkEdit.preview')}
        </Button>
      </div>

      {/* Step 2: what the preview said. */}
      {preview === undefined ? (
        <p className="text-muted-foreground mt-4 text-[12px]">{t('bulkEdit.previewHint')}</p>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          <p className="text-txt text-[12.3px]" data-testid="bulk-edit-summary">
            {t('bulkEdit.summary', {
              changed: preview.changedCount,
              unchanged: preview.unchangedCount,
              rejected: preview.rejections.length,
            })}
          </p>

          {overCap ? (
            <Alert variant="destructive">
              <AlertTitle>{t('bulkEdit.overCap.title')}</AlertTitle>
              <AlertDescription>
                {/* R10.6: both numbers, so narrowing the selection is an action the reader
                    can take rather than a guess. */}
                {t('bulkEdit.overCap.body', {
                  requested: preview.requestedCount,
                  max: preview.maxRecords,
                })}
              </AlertDescription>
            </Alert>
          ) : null}

          {preview.rows.length > 0 ? (
            <div className="max-h-[220px] overflow-auto">
              <Table data-testid="bulk-edit-rows">
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">{t('bulkEdit.col.initiative')}</TableHead>
                    <TableHead scope="col">{t('bulkEdit.col.before')}</TableHead>
                    <TableHead scope="col">{t('bulkEdit.col.after')}</TableHead>
                    <TableHead scope="col">{t('bulkEdit.col.approvalFlag')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((row) => (
                    <TableRow key={row.initiativeId}>
                      <TableCell className="text-txt">
                        <span className="font-mono text-[11.5px]">{row.initiativeRef}</span>
                        <span className="text-muted-foreground block truncate">{row.title}</span>
                      </TableCell>
                      <TableCell>
                        {row.changes
                          .map((change) => change.beforeLabel ?? t('bulkEdit.emptyValue'))
                          .join(', ')}
                      </TableCell>
                      <TableCell>
                        {row.changes
                          .map((change) => change.afterLabel ?? t('bulkEdit.emptyValue'))
                          .join(', ')}
                      </TableCell>
                      <TableCell>
                        {/* R10.12: applying sets the changed-after-approval flag on an
                            approved record. Shown per record, because it is a consequence
                            the reader is about to cause and cannot see afterwards without
                            opening each one. */}
                        {row.willFlagChangedAfterApproval ? (
                          <Badge variant="destructive">{t('bulkEdit.willFlag')}</Badge>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          {preview.rejections.length > 0 ? (
            <div className="max-h-[160px] overflow-auto">
              <Table data-testid="bulk-edit-rejections">
                <caption className="text-muted-foreground pb-1 text-left text-[11px]">
                  {t('bulkEdit.rejectionsCaption')}
                </caption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">{t('bulkEdit.col.initiative')}</TableHead>
                    <TableHead scope="col">{t('bulkEdit.col.reason')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rejections.map((rejection) => (
                    <TableRow key={rejection.initiativeId}>
                      <TableCell className="font-mono text-[11.5px]">
                        {/* R10.5: no reference travels for a record the requester may not
                            read, so the row names the reason alone. */}
                        {rejection.initiativeRef ?? t('bulkEdit.withheldReference')}
                      </TableCell>
                      <TableCell>
                        {t(`bulkEdit.rejection.${rejection.reason}`)}
                        {rejection.ruleIds.length > 0 ? ` (${rejection.ruleIds.join(', ')})` : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          {/* R10.14: the reason is recorded against the run and every record's history. */}
          <div className="flex flex-col gap-1">
            <label htmlFor="bulk-edit-reason" className="text-txt text-[11.5px] font-semibold">
              {t('bulkEdit.reasonLabel')}
            </label>
            <TextArea
              id="bulk-edit-reason"
              value={reason}
              maxLength={REASON_MAX_LENGTH}
              onChange={(event) => setReason(event.target.value)}
            />
            <p className="text-muted-foreground text-[11px]">{t('bulkEdit.reasonHint')}</p>
          </div>
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isApplying}>
          {t('bulkEdit.cancel')}
        </Button>
        <Button
          size="sm"
          data-testid="bulk-edit-apply"
          // No preview, no apply (R10.15). Nothing to change, no apply either: an apply of
          // zero records would write a run and a reason for no effect.
          disabled={
            preview === undefined ||
            preview.changedCount === 0 ||
            overCap ||
            !reasonValid ||
            isApplying
          }
          onClick={() => {
            onApply(trimmedReason);
          }}
        >
          {isApplying
            ? t('bulkEdit.applying')
            : t('bulkEdit.apply', { count: preview?.changedCount ?? 0 })}
        </Button>
      </div>
    </AriaModalDialog>
  );
}
